import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import Switch from 'components/ui/switch';
import { Slider } from 'components/ui/slider';
import { GetWallet } from 'modules/coins/api';

interface AiAssistCardProps {
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  threshold: number;
  onThresholdChange: (value: number) => void;
}

/** The threshold is what the grading pane reads from config.grading; AI grading draws on the coin balance. */
export const AiAssistCard: React.FC<AiAssistCardProps> = ({
  enabled,
  onEnabledChange,
  threshold,
  onThresholdChange,
}) => {
  const { t } = useTranslation();
  const { data: wallet } = useQuery({
    queryKey: ['coins', 'wallet'],
    queryFn: async () => (await GetWallet()).data,
    staleTime: 60_000,
  });

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-foreground">{t('ai_assisted_grading')}</p>
          <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{t('ai_assisted_grading_desc')}</p>
        </div>
        <Switch checked={enabled} onChange={onEnabledChange} />
      </div>

      {enabled && (
        <div className="mt-5 space-y-2">
          <div className="flex items-center gap-4">
            <label
              htmlFor="ai-confidence"
              className="shrink-0 text-sm font-medium text-foreground"
            >
              {t('ai_auto_accept_above')}
            </label>
            <div className="flex-1">
              <Slider
                id="ai-confidence"
                value={threshold}
                onChange={onThresholdChange}
                min={0}
                max={100}
                step={5}
                ariaLabel={t('ai_auto_accept_above')}
              />
            </div>
            <span className="w-14 shrink-0 text-right text-sm font-semibold leading-tight tabular-nums text-foreground">
              {threshold}%<span className="block text-xs font-normal text-muted-foreground">{t('ai_confidence_unit')}</span>
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{t('ai_below_threshold_note')}</p>
        </div>
      )}

      <div className="mt-4 flex items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
        <Coins className="h-3.5 w-3.5 shrink-0" />
        <span>
          {t('ai_grading_coin_note')}
          {wallet && (
            <>
              {' · '}
              {t('coin_balance', { count: wallet.balance })}
            </>
          )}
        </span>
      </div>
    </div>
  );
};

export default AiAssistCard;
